/** @jsx jsx */
import React, { Fragment, type PropsWithChildren, type ReactNode } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { usePreloadedQuery, graphql, type PreloadedQuery } from 'react-relay';
import { HydrationStatsReporter } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIsChromeless } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/chromeless/index.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { LayoutRemoteStateUpdater } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/layout-remote-state-updater/index.tsx';
import { RenderOnClient } from '@atlassian/jira-ssr-toolkit/src/render-on-client.tsx';
import { Main } from '@atlassian/jira-navigation-system/src/index.tsx';
import { IS_MODERN_SSR } from '@atlassian/jira-conditional-ssr/src/constants.tsx';
import { useJsmGracePeriodBanner } from '@atlassian/jira-jsm-grace-period-banner/src/controllers/index.tsx';
import { JSMGracePeriodBannerAsync } from '@atlassian/jira-jsm-grace-period-banner/src/ui/async.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { useProjectTheme } from '@atlassian/jira-project-theme-page-container/src/controllers/project-theme/index.tsx';
import { sharedLayout } from '@atlassian/jira-shared-layout/src/sharedLayout.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { isSystemNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/system/index.tsx';
import { isIssuesNavPath } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/get-navigation-path/issues/index.tsx';
import { JiraTopBannerPlacement } from '@atlassian/jira-top-banner-placement/src/index.tsx';
import { IntegrationsWhichRequireNavigationRoot } from '@atlassian/jira-shared-layout/src/ui/integrations/index.tsx';
import { getIsSoftwareThemingM2EnabledNoExposure } from '@atlassian/jira-project-theme-providers/src/utils/getIsSoftwareThemingEnabled.tsx';
import { PreviewPanel } from '@atlassian/jira-preview-panels/src/PreviewPanel.tsx';
import { HydrationFinishedSetter } from '@atlassian/jira-ssr-toolkit/src/HydrationFinishedSetter.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { BannerContent } from '../banner/BannerContent.tsx';
import { GlobalComponents } from '../global-components/index.tsx';
import { HorizontalNav } from '../horizontal-nav/index.tsx';
import { RightSidebar } from '../right-sidebar/index.tsx';
import { Sidebar } from '../sidebar/index.tsx';
import { TopNav } from '../topnav/index.tsx';
import { RedirectEntrypointContainer } from '../redirect/index.tsx';
import type { JiraSpaPageContainerQuery } from './__generated__/JiraSpaPageContainerQuery.graphql';
import { JiraPageLayout } from './JiraPageLayout.tsx';
import { GlobalThemeSetter } from './global-theme-setter/GlobalThemeSetter.tsx';
import { ProjectThemeSetter } from './project-theme-setter/ProjectThemeSetter.tsx';
import { PageContainerSuccessTracker } from './success-tracker/PageContainerSuccessTracker.tsx';
import { JiraSpaGlobalBottomRightCorner } from './JiraSpaGlobalBottomRightCorner.tsx';
import { JiraLogoPlaceholder } from './JiraLogoPlaceholder.tsx';

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="page-container-v2-main-wrapper">{children}</UFOSegment>
	),
	Fragment,
);

const mainWrapperStyles = cssMap({
	root: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		' > span[data-ep-placeholder-id="route_entry_point"]': {
			// EntryPointPlaceholder span element will stop the height from being 100%.
			// this makes content height can be set to 100% even if the span is present.
			// https://jplat.jira.atlassian.cloud/browse/BLU-3362
			display: 'contents',
		},
	},
	// Delete when cleaning up getIsSoftwareThemingM2Enabled
	themedOld: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		background: Tokens.ELEVATION_SURFACE,
		// When an image background is applied via the background css var the image
		// will be darkened in dark mode by blending with a black transparent background.
		backgroundBlendMode: 'darken',
	},
	themed: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		background: Tokens.ELEVATION_SURFACE,
		// When an image background is applied via the background css var the image
		// will be darkened in dark mode and lightened in light mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundBlendMode: Tokens.ELEVATION_SURFACE_BLEND_MODE,
	},
});

/**
 * These test IDs are used in pollinator tests. Do not change lightly.
 */
const ROOT_TEST_ID = 'page-layout.root';
const MAIN_TEST_ID = 'page-layout.main';

/**
 * The default page chrome for a Jira application.
 * We currently use an islands architecture, where each section of the layout
 * is relatively independent of one-another, and render a skeleton for each piece
 * of the page layout when it is loading.
 *
 * To achieve this, each section must suspend independent of each-other.
 * Be sure that each component rendered here has its own suspense boundary.
 *
 * @note
 * DO NOT USE FRAGMENTS IN THIS COMPONENT!
 * If the data for the fragment is not yet loaded (e.g., a lazy-loaded query, or when preload is not complete),
 *  this would cause the whole layout to suspend. When this happens, we wouldn't see the skeletons for each section.
 * Assuming there is no suspense boundary above this component, though, it would mean a broken app.
 */
const DefaultLayout = ({
	children,
	queryReference,
}: {
	children: ReactNode;
	queryReference: PreloadedQuery<JiraSpaPageContainerQuery>;
}) => {
	const [isChromeless] = useIsChromeless();
	const [isFullscreen] = useIsFullscreen();

	const showBanner = !isChromeless && !isFullscreen && (!__SERVER__ || IS_MODERN_SSR);
	const showTopNav = !isChromeless && !isFullscreen;
	const showSidebar = !isChromeless && !isFullscreen;
	const showHorizontalNav = !isChromeless;
	const showRightSidebar = !isChromeless && !isFullscreen;
	const showLayoutRemoteStateUpdater = !isChromeless && !isFullscreen;

	/**
	 * This query cannot contain anything except for fragment spreads.
	 * As soon as this includes any non-fragment content, it will need to wait for network to satisfy it,
	 * meaning it will suspend. We want the suspense to occur within each component,
	 * so please use fragments only!
	 */
	const data = usePreloadedQuery(
		graphql`
			query JiraSpaPageContainerQuery($cloudId: ID!) @preloadable {
				...BannerContent
			}
		`,
		queryReference,
	);

	return (
		<JiraPageLayout testId={ROOT_TEST_ID}>
			<RenderOnClient>
				<IntegrationsWhichRequireNavigationRoot />
			</RenderOnClient>
			{showBanner && (
				<JSErrorBoundary id="banner" packageName="jiraPageContainerV2" fallback="unmount">
					<Placeholder name="banner_skeleton" fallback={null}>
						<BannerContent bannerFragment={data} />
					</Placeholder>
				</JSErrorBoundary>
			)}
			{showTopNav && (
				<JSErrorBoundary id="top-nav" packageName="jiraPageContainerV2" fallback="unmount">
					<TopNav />
				</JSErrorBoundary>
			)}
			{showSidebar && (
				<JSErrorBoundary id="sidebar" packageName="jiraPageContainerV2" fallback="flag">
					<Sidebar />
				</JSErrorBoundary>
			)}
			<MainWrapper>
				{showHorizontalNav && <HorizontalNav />}
				{children}
			</MainWrapper>
			{showRightSidebar && (
				<JSErrorBoundary id="right-sidebar" packageName="jiraPageContainerV2" fallback="unmount">
					<RightSidebar />
				</JSErrorBoundary>
			)}
			{fg('jira-glance-panel') && (
				<JSErrorBoundary fallback="unmount" id="preview-panel" packageName="jiraPageContainerV2">
					<PreviewPanel />
				</JSErrorBoundary>
			)}
			{showLayoutRemoteStateUpdater && (
				<JSErrorBoundary
					packageName="jiraPageContainerV2"
					id="layout-remote-state-updater"
					fallback="unmount"
				>
					<LayoutRemoteStateUpdater />
				</JSErrorBoundary>
			)}
		</JiraPageLayout>
	);
};

const useRouterRoute = createRouterSelector((state) => state.route);

function MainWrapper(props: { children: ReactNode }) {
	const { hasTheming } = useProjectTheme();
	const { isEnabled: isJsmGracePeriodBannerEnabled } = useJsmGracePeriodBanner();

	const currentRoute = useRouterRoute();
	const isIssueOrSystemAdminPage =
		isSystemNavPath(currentRoute.path) || isIssuesNavPath(currentRoute.path);

	return (
		<Main testId={MAIN_TEST_ID}>
			<MaybeUFOSegment>
				<div
					data-vc="page-container-v2-main-wrapper"
					css={[
						mainWrapperStyles.root,
						hasTheming && !getIsSoftwareThemingM2EnabledNoExposure() && mainWrapperStyles.themedOld,
						hasTheming && getIsSoftwareThemingM2EnabledNoExposure() && mainWrapperStyles.themed,
					]}
				>
					{isJsmGracePeriodBannerEnabled && <JSMGracePeriodBannerAsync />}
					{isIssueOrSystemAdminPage && <JiraTopBannerPlacement />}
					{props.children}
				</div>
			</MaybeUFOSegment>
		</Main>
	);
}

const Layout = (props: {
	children: ReactNode;
	queryReference: PreloadedQuery<JiraSpaPageContainerQuery>;
}) => {
	const { LayoutOverrideComponent } = useRouterRoute();
	if (LayoutOverrideComponent !== undefined) {
		return <LayoutOverrideComponent>{props.children}</LayoutOverrideComponent>;
	}
	return <DefaultLayout {...props} />;
};

export const JiraSpaPageContainer = (props: {
	children: ReactNode;
	queryReference: PreloadedQuery<JiraSpaPageContainerQuery> | null | undefined;
}) => (
	<>
		{fg('redirect_all_new_jira_users_to_user_segmentation') && <RedirectEntrypointContainer />}
		<GlobalThemeSetter />
		<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
			<ProjectThemeSetter />
			<Placeholder
				name="spa_layout"
				fallback={<JiraLogoPlaceholder reason="spa_layout_fallback" />}
			>
				{props?.queryReference ? (
					<Layout queryReference={props.queryReference}>{props.children}</Layout>
				) : (
					<JiraLogoPlaceholder reason="spa_no_query_reference" />
				)}
			</Placeholder>
			<GlobalComponents sharedGlobalComponents={sharedLayout.globalComponents} />
			<JiraSpaGlobalBottomRightCorner />
			<PageContainerSuccessTracker />
			<HydrationStatsReporter />
			{fg('jfp-magma-4013-fix-hydration-edition-awareness') && <HydrationFinishedSetter />}
		</ContextualAnalyticsData>
	</>
);
